import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, ConfirmModal, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type {
  TotvsProductLink,
  TotvsProductLinkAction,
  TotvsProvider,
} from '../../domain/entities/totvs.entity';
import {
  useDeleteTotvsProductLink,
  useTotvsProductLinks,
} from '../hooks/use-totvs-product-links';
import {
  formatDateTime,
  getApiErrorMessage,
  Mono,
  ProductLinkActionBadge,
  ProductLinkEnabledBadge,
  TotvsProviderBadge,
} from '../display';
import { TotvsProductLinkFormModal } from '../components/TotvsProductLinkFormModal';
import {
  countActiveTotvsProductLinkFilters,
  EMPTY_TOTVS_PRODUCT_LINK_FILTERS,
  TotvsProductLinkFiltersModal,
  type TotvsProductLinkFilters,
} from '../components/TotvsProductLinkFiltersModal';
import { useCanWriteTotvs } from '../hooks/use-totvs-permissions';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;
const INCLUDE_DISABLED_PARAM = 'includeDisabled';

export function TotvsProductLinksPage() {
  const canWrite = useCanWriteTotvs();
  // Página, filtros e "incluir inativos" vivem na URL (não em useState) —
  // ver primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Aplicar/limpar
  // filtro e alternar o checkbox resetam a página; cada mudança vai num
  // único setSearchParams (ver aviso em use-page-query-param.ts).
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(
    searchParams,
    EMPTY_TOTVS_PRODUCT_LINK_FILTERS,
  );
  const includeDisabled = searchParams.get(INCLUDE_DISABLED_PARAM) !== 'false';
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TotvsProductLink | null>(null);
  const [deleting, setDeleting] = useState<TotvsProductLink | null>(null);

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };

  const setFiltersAndResetPage = (next: TotvsProductLinkFilters) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_TOTVS_PRODUCT_LINK_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const setIncludeDisabledAndResetPage = (checked: boolean) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (checked) params.delete(INCLUDE_DISABLED_PARAM);
        else params.set(INCLUDE_DISABLED_PARAM, 'false');
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const { data, isLoading, refetch, isFetching } = useTotvsProductLinks({
    page,
    perPage: PER_PAGE,
    provider: filters.provider
      ? (filters.provider as TotvsProvider)
      : undefined,
    providerAccountId: filters.providerAccountId || undefined,
    productExternalId: filters.productExternalId || undefined,
    offerExternalId: filters.offerExternalId || undefined,
    action: filters.action
      ? (filters.action as TotvsProductLinkAction)
      : undefined,
    idAreaInteresse: filters.idAreaInteresse || undefined,
    codColigada: filters.codColigada ? Number(filters.codColigada) : undefined,
    codFilial: filters.codFilial ? Number(filters.codFilial) : undefined,
    idPs: filters.idPs || undefined,
    enabled: includeDisabled ? undefined : true,
  });
  const deleteLink = useDeleteTotvsProductLink();

  const links = useMemo(() => data?.items ?? [], [data?.items]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const activeFilterCount = countActiveTotvsProductLinkFilters(filters);

  const handleApplyFilters = (next: TotvsProductLinkFilters) => {
    setFiltersAndResetPage(next);
  };

  const handleClearFilters = () => {
    setFiltersAndResetPage(EMPTY_TOTVS_PRODUCT_LINK_FILTERS);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteLink.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        deleteLink.reset();
      },
    });
  };

  const columns: ColumnDef<TotvsProductLink>[] = [
    {
      key: 'provider',
      header: 'Gateway',
      render: (row) => (
        <div className="space-y-1">
          <TotvsProviderBadge provider={row.provider} />
          <div className="text-(--color-text-secondary) text-xs">
            {row.providerAccountId ? (
              <>
                Conta: <Mono>{row.providerAccountId}</Mono>
              </>
            ) : (
              'Curinga (conta não definida)'
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'externalIds',
      header: 'Produto/oferta externa',
      render: (row) => (
        <div className="space-y-1">
          <div>
            <span className="text-(--color-text-secondary) text-xs">
              Produto:{' '}
            </span>
            <Mono>{row.productExternalId}</Mono>
          </div>
          <div>
            <span className="text-(--color-text-secondary) text-xs">
              Oferta:{' '}
            </span>
            <Mono>{row.offerExternalId}</Mono>
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Destino',
      render: (row) => <ProductLinkActionBadge action={row.action} />,
    },
    {
      key: 'rm',
      header: 'RM/TOTVS',
      // Em `ignore` não há campo do RM para mostrar — o que o operador quer ler
      // ali é a justificativa, que é o registro da decisão.
      render: (row) =>
        row.action === 'ignore' ? (
          <span className="text-(--color-text-secondary) text-sm">
            {row.ignoreReason ?? '—'}
          </span>
        ) : (
          <div className="space-y-1 text-sm">
            <div className="text-(--color-text-primary)">
              IDAREAINTERESSE: <Mono>{row.idAreaInteresse}</Mono>
            </div>
            <div className="text-(--color-text-secondary) text-xs">
              Coligada {row.codColigada} · Filial {row.codFilial} · IDPS{' '}
              {row.idPs}
            </div>
          </div>
        ),
    },
    {
      key: 'enabled',
      header: 'Situação',
      render: (row) => <ProductLinkEnabledBadge enabled={row.enabled} />,
    },
    {
      key: 'updatedAt',
      header: 'Atualizado em',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.updatedAt)}
        </span>
      ),
    },
    // Só quem pode escrever vê a coluna de ações — o viewer apenas visualiza.
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            render: (row) => (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditing(row)}>
                  Editar
                </Button>
                {row.enabled && (
                  <Button variant="danger" onClick={() => setDeleting(row)}>
                    Desativar
                  </Button>
                )}
              </div>
            ),
          } satisfies ColumnDef<TotvsProductLink>,
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            TOTVS · De-para de produtos (só estratégia MCB)
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Mapeia IDs de produto/oferta do Guru/Hotmart para IDAREAINTERESSE,
            CODCOLIGADA, CODFILIAL e IDPS usados na matrícula via fórmula visual
            do RM — ou marca o produto como <strong>ignorado</strong>, quando a
            venda não deve virar matrícula.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          {canWrite && (
            <Button onClick={() => setCreating(true)}>Criar de-para</Button>
          )}
        </div>
      </div>

      <div className="space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
        <p>
          Somente compras de ofertas com a tag <Mono>[TV]</Mono> no nome são
          processadas pelo TOTVS. De-paras cadastrados aqui só têm efeito para
          essas ofertas — as demais seguem pelo fluxo normal do Delivery.
        </p>
        <p>
          <strong>Este de-para só vale para a estratégia MCB</strong>{' '}
          (SaveRecord/ZMDSALUNO) — modo atual. Se a matrícula estiver rodando
          via <strong>worknow</strong> (Fórmulas 7/5, modo de contingência), o
          que estiver cadastrado aqui não tem efeito nenhum na matrícula.
        </p>
      </div>

      <TotvsProductLinkFiltersModal
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition"
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>

          <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabledAndResetPage(e.target.checked)}
            />
            Incluir inativos
          </label>
        </div>

        {activeFilterCount > 0 && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-(--color-text-secondary)">
              {activeFilterCount === 1
                ? '1 filtro aplicado'
                : `${activeFilterCount} filtros aplicados`}
            </p>
            <button
              onClick={handleClearFilters}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        <Table
          columns={columns}
          data={links}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="Nenhum de-para TOTVS encontrado."
          striped
        />

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data
              ? `${data.total} ${data.total === 1 ? 'de-para encontrado' : 'de-paras encontrados'}`
              : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>

      <TotvsProductLinkFormModal
        open={creating}
        onClose={() => setCreating(false)}
      />
      <TotvsProductLinkFormModal
        open={!!editing}
        link={editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmModal
        open={!!deleting}
        onClose={() => {
          setDeleting(null);
          deleteLink.reset();
        }}
        onConfirm={confirmDelete}
        title="Desativar de-para TOTVS"
        description={`Desativar o de-para de ${deleting?.productExternalId ?? ''}/${deleting?.offerExternalId ?? ''}? Novos contratos não usarão mais esse mapeamento.`}
        confirmLabel="Desativar"
        loading={deleteLink.isPending}
        errorMessage={
          deleteLink.error
            ? getApiErrorMessage(deleteLink.error, 'Falha na operação')
            : null
        }
      />
    </div>
  );
}
